import { Injectable, Injector } from '@angular/core';
import { AuthService } from './auth.service';
import { EnumPermissao } from '../enums/permissoes.enum';
import { ToastrService } from 'ngx-toastr';
import { BaseService } from '../infra/base-service';
import { HttpClient } from '@angular/common/http';
import { ResponsePermissoesUsuario, OptionPermissao } from '../interfaces/usuario/permissao.interface';
import { Usuario } from '../interfaces/usuario/usuario.interface';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PermissaoService extends BaseService {
  private readonly STORAGE_KEY = environment.todasPermissoesKey;
  private readonly STORAGE_KEY_TEMPO = environment.todasPermissoesTempoExpiracaoKey;
  private readonly TEMPO_EXPIRACAO = environment.tempoExpiracaoCachePermissoesMinutos * 60 * 1000;
  private permissoesCache: OptionPermissao[] = [];
  
  constructor(
    http: HttpClient, 
    toastr: ToastrService, 
    private injector: Injector
  ) {
    super(http, toastr);
  }

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  buscarPermissoesUsuario(idUsuario: number, idCliente: number): Promise<ResponsePermissoesUsuario> {
    return this.get<ResponsePermissoesUsuario>(`usuario/permissoes?idUsuario=${idUsuario}&idCliente=${idCliente}`);
  }

  private cacheExpirado(timestamp: number): boolean {
    return Date.now() - timestamp > this.TEMPO_EXPIRACAO;
  }

  private salvarCache(permissoes: OptionPermissao[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(permissoes));
      localStorage.setItem(this.STORAGE_KEY_TEMPO, Date.now().toString());
      this.permissoesCache = permissoes;
    } catch (error) {
      console.error('Erro ao salvar cache de permissões:', error);
    }
  }

  private limparCache(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_KEY_TEMPO);
    this.permissoesCache = [];
  }

  async obterTodasPermissoes(): Promise<OptionPermissao[]> {
    try {
      const tempoStr = localStorage.getItem(this.STORAGE_KEY_TEMPO);
      const tempo = tempoStr ? parseInt(tempoStr) : 0;

      if (this.permissoesCache.length > 0 && !this.cacheExpirado(tempo)) {
        return this.permissoesCache;
      }

      const permissoes = await this.get<OptionPermissao[]>('usuario/listarpermissoes');
      const permissoesFiltradas = permissoes.filter(p => p.idPermissao !== EnumPermissao.ConsultarMargem);
      this.salvarCache(permissoesFiltradas);
      return permissoesFiltradas;
    } catch (error) {
      console.error('Erro ao obter permissões:', error);
      return this.permissoesCache;
    }
  }

  resetarPermissoes(): void {
    this.limparCache();
  }

  async salvarPermissoesUsuario(permissoes: Usuario): Promise<void> {
    return this.post<void>('usuario/salvar-permissoes', permissoes);
  }

  hasPermissionComponente(permissao: EnumPermissao): boolean {
    return this.authService.hasPermission(permissao);
  }

  hasPermission(permission: EnumPermissao): boolean {
    return this.authService.hasPermission(permission);
  }
  
  canRealizarBuscaAvancada(): boolean {
    return this.hasPermission(EnumPermissao.RealizarBuscaAvancada);
  }
  
  canFazerSegmentacao(): boolean {
    return this.hasPermission(EnumPermissao.FazerSegmentacao);
  }
  
  canGerarListaContatos(): boolean {
    return this.hasPermission(EnumPermissao.GerarListaContatos);
  }
  
  canVisualizarContatos(): boolean {
    return this.hasPermission(EnumPermissao.VisualizarContatos);
  }
  
  canExportarLista(): boolean {
    return this.hasPermission(EnumPermissao.ExportarLista);
  }
  
  canConsultarMargem(): boolean {
    return this.hasPermission(EnumPermissao.ConsultarMargem);
  }
  
  canCriarNovosUsuarios(): boolean {
    return this.hasPermission(EnumPermissao.CriarNovosUsuarios);
  }
  
  canFazerRecarga(): boolean {
    return this.hasPermission(EnumPermissao.FazerRecarga);
  }
  
  canVisualizarSegmentacoesOutrosUsuarios(): boolean {
    return this.hasPermission(EnumPermissao.VisualizarSegmentacoesOutrosUsuarios);
  }
}